import React, { memo, useCallback, useEffect, useState, useContext } from 'react';
import { Box, Drawer, List, ListItem, ListItemButton, ListItemText, Divider, Typography, Button, IconButton, Collapse, Tooltip } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu as MenuIcon, Close as CloseIcon, ExpandLess, ExpandMore, CheckCircle as CheckIcon, RadioButtonUnchecked as UncompletedIcon, FiberManualRecord as CurrentIcon } from '@mui/icons-material';
import { useCustomer } from '../pages/CustomerContext';
import { luxuryTextures, withLuxuryTexture } from '../styles/luxuryTextures';
import { useTheme } from '../contexts/ThemeContext';
import { PageTransition } from '../components/PageTransitionsLight';

// Redesigned step navigation component with proper visual hierarchy
const StepNavItem = memo(({ item, status, isClickable, onNavigate, index, collapsed, isLastInGroup, progressLineColor }) => {
  const getStepIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckIcon sx={{ fontSize: 20, color: '#B8860B' }} />;
      case 'current':
        return (
          <CurrentIcon 
            sx={{ 
              fontSize: 20, 
              color: '#B8860B',
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
                '50%': {
                  transform: 'scale(1.1)',
                  opacity: 0.8,
                },
                '100%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
              },
            }} 
          />
        );
      case 'upcoming':
      default:
        return <UncompletedIcon sx={{ fontSize: 20, color: '#bbb' }} />;
    }
  };

  const getTextColor = (status) => {
    switch (status) {
      case 'completed':
        return '#B8860B';
      case 'current':
        return '#B8860B';
      case 'upcoming':
      default:
        return '#888';
    }
  };

  const getHoverColor = (status) => {
    switch (status) {
      case 'completed':
      case 'current':
        return 'rgba(184, 134, 11, 0.08)';
      case 'upcoming':
      default:
        return 'transparent';
    }
  };

  return (
    <Tooltip 
      title={collapsed ? item.text : item.description} 
      placement="right"
      arrow
      sx={{
        '& .MuiTooltip-tooltip': {
          bgcolor: '#333',
          color: '#fff',
          fontSize: '0.75rem',
          maxWidth: 200,
        },
        '& .MuiTooltip-arrow': {
          color: '#333',
        },
      }}
    >
      <ListItem 
        disablePadding 
        sx={{ 
          mb: 0.5, 
          position: 'relative',
          '&::before': !collapsed && index > 0 ? {
            content: '""',
            position: 'absolute',
            left: 22,
            top: -12,
            width: '2px',
            height: 24,
            background: 'linear-gradient(180deg, #B8860B, #ddd)',
            zIndex: 0,
          } : {},
          '&::after': !collapsed && !isLastInGroup ? {
            content: '""',
            position: 'absolute',
            left: 22,
            bottom: -12,
            width: '2px',
            height: 24,
            background: progressLineColor || '#ddd',
            zIndex: 0,
          } : {},
        }}
      >
        <ListItemButton
          onClick={() => isClickable && onNavigate(item.path)}
          disabled={!isClickable}
          sx={{
            minHeight: 48,
            px: collapsed ? 0 : 2,
            py: 1,
            mx: collapsed ? 0 : 1,
            borderRadius: collapsed ? 0 : '8px',
            justifyContent: collapsed ? 'center' : 'flex-start',
            cursor: isClickable ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s ease',
            position: 'relative',
            zIndex: 1,
            '&:hover': isClickable ? {
              backgroundColor: getHoverColor(status),
              transform: collapsed ? 'scale(1.05)' : 'translateX(4px)',
              '& .step-text': {
                color: status === 'upcoming' ? '#B8860B' : undefined,
              },
            } : {},
            '&:disabled': {
              opacity: 0.6,
            },
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: collapsed ? 0 : 2,
            width: '100%'
          }}>
            {/* Step Icon */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              minWidth: 24,
              height: 24,
              position: 'relative',
              zIndex: 2,
            }}>
              {getStepIcon(status)}
            </Box>
            
            {/* Step Text */}
            {!collapsed && (
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  className="step-text"
                  sx={{
                    color: getTextColor(status),
                    fontWeight: status === 'current' ? 600 : status === 'completed' ? 500 : 400,
                    fontSize: '0.875rem',
                    lineHeight: 1.3,
                    transition: 'color 0.2s ease',
                  }}
                >
                  {item.text}
                </Typography>
              </Box>
            )}
          </Box>
        </ListItemButton>
      </ListItem>
    </Tooltip>
  );
});

const drawerWidth = 280; // Expanded width
const collapsedDrawerWidth = 60; // Collapsed width
const headerHeight = 64;

function CustomerLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { customer, clearCustomer } = useCustomer();
  const { isDarkMode } = useTheme();
  const [role, setRole] = useState(localStorage.getItem('role'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(['start']); // Start with 'start' group expanded
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // On mobile, drawer should be closed by default
      if (mobile && drawerOpen) {
        setDrawerOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [drawerOpen]);
  

  useEffect(() => {
    const selectionPathRegex = /^\/(selection|location|$)/;
    const isSelectionPage = selectionPathRegex.test(location.pathname);

    if (
      role === 'Sales' &&
      isSelectionPage &&
      !localStorage.getItem('customerId')
    ) {
      if (location.pathname !== '/sales-dashboard') {
        navigate('/sales-dashboard');
      }
    }
  }, [role, location.pathname, navigate]);

  useEffect(() => {
    setRole(localStorage.getItem('role'));
  }, []);

  const handleClearCustomer = () => {
    clearCustomer();
    setTimeout(() => {
      navigate('/sales-dashboard');
    }, 100);
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => 
      prev.includes(groupName) 
        ? prev.filter(g => g !== groupName)
        : [...prev, groupName]
    );
  };



  // Organized menu items with new pricing matrix categories
  const menuItems = [
    // Getting Started
    { text: 'Mortgage Details', path: 'mortgage', group: 'start', order: 1, description: 'Set up financing and mortgage details' },
    { text: 'House Selection', path: 'selection', group: 'start', order: 2, description: 'Choose your house plan and lot' },

    // Structure
    { text: 'Elevation', path: 'location/elevation', group: 'structure', order: 3, description: 'Exterior elevation style' },
    { text: 'Lot Type', path: 'location/lot-type', group: 'structure', order: 4, description: 'Lot type and configuration' },
    { text: 'Patio / Deck', path: 'location/patio-deck', group: 'structure', order: 5, description: 'Outdoor living space options' },
    { text: 'Basement', path: 'location/basement', group: 'structure', order: 6, description: 'Basement finishing options' },
    { text: 'Garage', path: 'location/garage', group: 'structure', order: 7, description: 'Garage features and storage' },

    // Interior
    { text: 'Ceilings', path: 'location/ceilings', group: 'interior', order: 8, description: 'Ceiling heights and finishes' },
    { text: 'Windows', path: 'location/windows', group: 'interior', order: 9, description: 'Window styles and upgrades' },
    { text: 'Flooring', path: 'location/flooring', group: 'interior', order: 10, description: 'Flooring materials and finishes' },
    { text: 'Cabinets', path: 'location/cabinets', group: 'interior', order: 11, description: 'Cabinet styles and finishes' },

    // Exterior
    { text: 'Siding', path: 'location/siding', group: 'exterior', order: 12, description: 'Exterior siding materials and colors' },
    { text: 'Roof / Soffit', path: 'location/roof-soffit', group: 'exterior', order: 13, description: 'Roofing and soffit details' },
    { text: 'Porch', path: 'location/porch', group: 'exterior', order: 14, description: 'Porch selections' },
    { text: 'Stone', path: 'location/stone', group: 'exterior', order: 15, description: 'Stone accents and features' },
    { text: 'Front Door', path: 'location/front-door', group: 'exterior', order: 16, description: 'Front door style and color' },

    // Kitchen
    { text: 'Kitchen Backsplash', path: 'location/kitchen-backsplash', group: 'kitchen', order: 17, description: 'Backsplash tile and material' },
    { text: 'Kitchen Faucet', path: 'location/kitchen-faucet', group: 'kitchen', order: 18, description: 'Kitchen faucet style and finish' },
    { text: 'Countertops', path: 'location/countertops', group: 'kitchen', order: 19, description: 'Countertop materials and edges' },
    { text: 'Sinks', path: 'location/sinks', group: 'kitchen', order: 20, description: 'Sink styles and configurations' },
    { text: 'Appliances', path: 'location/appliances', group: 'kitchen', order: 21, description: 'Appliance packages and upgrades' },

    // Bathrooms
    { text: 'Bathroom Fixtures', path: 'location/bathroom-fixtures', group: 'bathrooms', order: 22, description: 'Fixtures and finishes' },
    { text: 'Shower', path: 'location/shower', group: 'bathrooms', order: 23, description: 'Shower configurations and tile' },
    { text: 'Toilets', path: 'location/toilets', group: 'bathrooms', order: 24, description: 'Toilet styles and upgrades' },

    // Features
    { text: 'Lighting', path: 'location/lighting', group: 'features', order: 25, description: 'Lighting fixtures and packages' },
    { text: 'Fireplace', path: 'location/fireplace', group: 'features', order: 26, description: 'Fireplace style and surround' },
    { text: 'Interior', path: 'location/interior', group: 'features', order: 27, description: 'Interior trim and finishes' },
    { text: 'Door Handles', path: 'location/door-handles', group: 'features', order: 28, description: 'Door hardware finishes' },
    { text: 'Garage Door', path: 'location/garage-door', group: 'features', order: 29, description: 'Garage door style and material' },
    { text: 'Mechanical', path: 'location/mechanical', group: 'features', order: 30, description: 'HVAC and mechanical upgrades' },

    // Final
    { text: 'Summary', path: 'selection-summary', group: 'final', order: 31, description: 'Review and finalize selections' },
  ];

  // Calculate step completion status
  const getStepStatus = useCallback((item) => {
    const currentIndex = menuItems.findIndex(menuItem => location.pathname.includes(menuItem.path));
    const itemIndex = menuItems.findIndex(menuItem => menuItem.path === item.path);
    
    if (itemIndex < currentIndex) {
      return 'completed';
    } else if (itemIndex === currentIndex) {
      return 'current';
    } else {
      return 'upcoming';
    }
  }, [location.pathname, menuItems]);

  // Get progress line color based on step status
  const getProgressLineColor = useCallback((currentStatus, nextStatus) => {
    if (currentStatus === 'completed' && nextStatus === 'completed') {
      return '#B8860B';
    } else if (currentStatus === 'completed' && nextStatus === 'current') {
      return 'linear-gradient(180deg, #B8860B, #B8860B)';
    } else if (currentStatus === 'current' && nextStatus === 'upcoming') {
      return 'linear-gradient(180deg, #B8860B, #ddd)';
    } else {
      return '#ddd';
    }
  }, []);

  // Check if step is clickable (completed or current)
  const isStepClickable = useCallback((item) => {
    const status = getStepStatus(item);
    return status === 'completed' || status === 'current';
  }, [getStepStatus]);

  const handleNavigate = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  const renderMenuSection = (items, groupName, startIndex) => {
    const groupLabels = {
      start: 'Getting Started',
      structure: 'Structure',
      interior: 'Interior',
      exterior: 'Exterior',
      kitchen: 'Kitchen',
      bathrooms: 'Bathrooms',
      features: 'Features',
      final: 'Final Steps',
    };

    const isExpanded = expandedGroups.includes(groupName);

    return (
      <Box key={groupName} sx={{ mb: 2 }}>
        {drawerOpen && (
          <ListItemButton
            onClick={() => toggleGroup(groupName)}
            sx={{
              px: 2,
              py: 1,
              mx: 1,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'rgba(184, 134, 11, 0.08)',
              },
            }}
          >
            <Typography 
              variant="overline" 
              sx={{ 
                fontSize: '0.75rem',
                fontWeight: 700,
                color: '#B8860B',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                flexGrow: 1
              }}
            >
              {groupLabels[groupName]}
            </Typography>
            {isExpanded ? <ExpandLess sx={{ color: '#B8860B' }} /> : <ExpandMore sx={{ color: '#B8860B' }} />}
          </ListItemButton>
        )}
        <Collapse in={drawerOpen && isExpanded} timeout="auto" unmountOnExit>
          {items.map((item, index) => {
            const status = getStepStatus(item);
            const isClickable = isStepClickable(item);
            const isLastInGroup = index === items.length - 1;
            const nextItem = items[index + 1];
            const nextStatus = nextItem ? getStepStatus(nextItem) : 'upcoming';
            
            return (
              <StepNavItem
                key={item.path}
                item={item}
                status={status}
                isClickable={isClickable}
                onNavigate={handleNavigate}
                index={startIndex + index}
                collapsed={false}
                isLastInGroup={isLastInGroup}
                progressLineColor={getProgressLineColor(status, nextStatus)}
              />
            );
          })}
        </Collapse>
        
        {/* Show collapsed icons when drawer is collapsed */}
        {!drawerOpen && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, px: 1 }}>
            {items.slice(0, 3).map((item, index) => {
              const status = getStepStatus(item);
              const isClickable = isStepClickable(item);
              const nextItem = items[index + 1];
              const nextStatus = nextItem ? getStepStatus(nextItem) : 'upcoming';
              
              return (
                <StepNavItem
                  key={item.path}
                  item={item}
                  status={status}
                  isClickable={isClickable}
                  onNavigate={handleNavigate}
                  index={startIndex + index}
                  collapsed={true}
                  isLastInGroup={false}
                  progressLineColor={getProgressLineColor(status, nextStatus)}
                />
              );
            })}
            {items.length > 3 && (
              <Box sx={{ textAlign: 'center', py: 1 }}>
                <Typography variant="caption" sx={{ color: '#B8860B' }}>•••</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  // Group menu items
  const groupedMenus = menuItems.reduce((acc, item) => {
    if (!acc[item.group]) acc[item.group] = [];
    acc[item.group].push(item);
    return acc;
  }, {});

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        sx={{
          width: drawerOpen ? drawerWidth : collapsedDrawerWidth,
          flexShrink: 0,
          transition: 'width 0.3s ease',
          '& .MuiDrawer-paper': {
            width: drawerOpen ? drawerWidth : collapsedDrawerWidth,
            boxSizing: 'border-box',
            top: isMobile ? 0 : headerHeight,
            height: isMobile ? '100vh' : `calc(100vh - ${headerHeight}px)`,
            zIndex: isMobile ? 1300 : 1200,
            transition: 'width 0.3s ease',
            overflowX: 'hidden',
            ...withLuxuryTexture({
              background: isDarkMode 
                ? 'linear-gradient(180deg, rgba(30, 30, 30, 0.98) 0%, rgba(20, 20, 20, 0.95) 100%)'
                : 'linear-gradient(180deg, #fefefe 0%, #f8f6f0 100%)',
              borderRight: '2px solid rgba(212, 160, 23, 0.1)',
              boxShadow: '4px 0 20px rgba(212, 160, 23, 0.08)',
            },
            luxuryTextures.noise[isDarkMode ? 'dark' : 'light'],
            luxuryTextures.layeredGradient.luxury
            ),
            position: 'fixed',
          },
        }}
        variant={isMobile ? 'temporary' : 'permanent'}
        anchor="left"
        open={isMobile ? drawerOpen : true}
        onClose={() => setDrawerOpen(false)}
      >
        {/* Hamburger Toggle Button */}
        <Box sx={{ display: 'flex', justifyContent: drawerOpen ? 'flex-end' : 'center', p: 1 }}>
          <IconButton
            onClick={toggleDrawer}
            sx={{
              color: '#d4a017',
              '&:hover': {
                backgroundColor: 'rgba(212, 160, 23, 0.08)',
              },
            }}
          >
            {drawerOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </Box>
        {role === 'Sales' && customer?.id && drawerOpen && (
          <Box 
            sx={{ 
              px: 2, 
              py: 2, 
              mb: 1,
              background: 'linear-gradient(135deg, rgba(212, 160, 23, 0.1) 0%, rgba(184, 134, 11, 0.05) 100%)',
              borderRadius: '12px',
              mx: 1,
              mt: 1,
              border: '1px solid rgba(212, 160, 23, 0.15)'
            }}
          >
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontSize: '0.75rem', 
                color: '#8b6914', 
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 0.5
              }}
            >
              Editing Customer
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.8rem', 
                color: '#2c2c2c',
                fontWeight: 500,
                mb: 1.5
              }}
            >
              {customer.id}
              {customer.email ? ` – ${customer.email}` : ''}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearCustomer}
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                borderColor: '#dc143c',
                color: '#dc143c',
                borderRadius: '8px',
                textTransform: 'none',
                py: 0.5,
                px: 1.5,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: '0',
                  height: '0',
                  borderRadius: '50%',
                  background: 'rgba(220, 20, 60, 0.1)',
                  transform: 'translate(-50%, -50%)',
                  transition: 'width 0.6s, height 0.6s',
                },
                '&:hover': { 
                  bgcolor: 'rgba(220, 20, 60, 0.05)', 
                  borderColor: '#dc143c',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(220, 20, 60, 0.3)',
                  '&::before': {
                    width: '300px',
                    height: '300px',
                  },
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              Exit Customer
            </Button>
          </Box>
        )}
        
        <Box sx={{ overflowY: 'auto', height: '100%', pb: 2, flex: 1 }}>
          {(() => {
            let currentIndex = 0;
            return Object.entries(groupedMenus).map(([groupName, items]) => {
              const section = renderMenuSection(items, groupName, currentIndex);
              currentIndex += items.length;
              return section;
            });
          })()}
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          p: { xs: 2, sm: 3 },
          pt: { xs: 9, sm: 11 },
          bgcolor: '#fefefe',
          ml: isMobile ? 0 : (drawerOpen ? `${drawerWidth}px` : `${collapsedDrawerWidth}px`),
          minHeight: 'calc(100vh - 64px)',
          transition: 'margin-left 0.3s ease',
          pb: 3, // Normal padding
        }}
      >
        <Box
          sx={{
            maxWidth: 1000, // Increased from 800 to use more space
            width: '100%',
            margin: '0 auto !important',
          }}
        >
          <PageTransition variant="fadeScale">
            {children}
          </PageTransition>
        </Box>
      </Box>

    </Box>
  );
}

export default CustomerLayout;